/**
 * 瀑布流组件
 * 实现双列瀑布流布局，自动计算每个元素的高度并分配到左右两列中
 * 使用虚拟列表优化性能，只渲染可视区域内的元素
 */
import { pxtorem } from "@/utils/pxtorem";
import React, { useEffect, useRef, useState } from "react";
import VirtialList from "../virtual-list/index";
import styles from "./waterfall.module.less";

/**
 * 瀑布流组件属性接口
 */
interface Props {
  // 要渲染的数据列表
  dataList: any[];
  
  // 子项渲染函数，用于自定义每个数据项的渲染方式
  childRender: React.FC<any>;
  
  // 元素之间的间距配置，x为水平间距，y为垂直间距
  margin?: { x: number; y: number; };
  
  // 虚拟列表预加载高度
  padding?: number;
  
  // 重置容器高度的回调函数
  resetHeight?: (height: number) => void;

  // 是否需要渲染子项的判断函数，返回true表示需要渲染
  shouldRenderChild?: (item: any) => boolean;
}

/**
 * 瀑布流组件
 * 实现原理：
 * 1. 先渲染所有元素但设为不可见，获取每个元素的高度
 * 2. 将元素分配到左右两列，保持两列高度尽量平衡
 * 3. 使用虚拟列表优化性能
 * @param props 组件属性
 */
const Waterfall: React.FC<Props> = (props: Props) => {
  // 解构props获取必要参数
  const { dataList, childRender, margin = { x: 8, y: 8 }, padding, resetHeight } = props;
  // 获取根元素的font-size，用于rem计算
  const ratio: any = document.documentElement.style.fontSize.split("px")[0];
  
  // 左列数据数组
  const [leftArr, setLeftArr] = useState<any>([]);
  // 右列数据数组
  const [rightArr, setRightArr] = useState<any>([]);
  // 左列每个元素的高度数组
  const [leftHeightArr, setLeftHeightArr] = useState<any>([]);
  // 右列每个元素的高度数组
  const [rightHeightArr, setRightHeightArr] = useState<any>([]);
  // 左列总高度引用
  const leftHeightRef = useRef<number>(0);
  // 右列总高度引用
  const rightHeightRef = useRef<number>(0);
  // 瀑布流容器的最大高度
  const [maxHeight, setMaxHeight] = useState<number>(0);
  // 所有元素的高度列表
  const [domList, setDomList] = useState<any[]>([]);

  /**
   * 当元素高度列表更新时，重新分配元素到左右两列
   * 采用贪心算法，始终将新元素添加到当前高度较低的一列
   * 以保持两列高度尽量平衡
   */
  useEffect(() => {
    // 获取已分配的元素数量
    const start = leftArr.length + rightArr.length;

    // 遍历未分配的元素
    for (let i = start; i < domList.length; i++) {
      // 将元素添加到高度较低的一列
      if (leftHeightRef.current <= rightHeightRef.current) {
        // 添加到左列
        leftArr.push(dataList[i]);
        leftHeightArr.push(domList[i]);
        // 更新左列总高度，转换为rem单位
        leftHeightRef.current += (domList[i] + margin.y) / Number(ratio);
      } else {
        // 添加到右列
        rightArr.push(dataList[i]);
        rightHeightArr.push(domList[i]);
        // 更新右列总高度，转换为rem单位
        rightHeightRef.current += (domList[i] + margin.y) / Number(ratio);
      }
    }
    // 设置容器的最大高度为两列中的较大值
    setMaxHeight(Math.max(leftHeightRef.current, rightHeightRef.current));
    // 更新状态，触发重新渲染
    setLeftArr([...leftArr]);
    setRightArr([...rightArr]);
    setLeftHeightArr([...leftHeightArr]);
    setRightHeightArr([...rightHeightArr]);
    // 调用外部传入的重置高度回调函数
    resetHeight ? resetHeight(Math.max(leftHeightRef.current, rightHeightRef.current)) : "";
  }, [domList]);

  /**
   * 当数据列表更新时，获取所有元素的实际高度
   * 这是瀑布流布局的关键步骤，需要先渲染元素才能获取其高度
   */
  useEffect(() => {
    // 获取所有原始卡片元素
    const items: any = document.getElementsByClassName(styles.originCard);
    // 复制当前的高度列表
    let arr = [...domList];
    // 遍历所有卡片元素，获取其高度
    for (let item of items) {
      // 如果该索引位置还没有记录高度，则添加
      if (!arr[item.dataset.index]) {
        // 使用getBoundingClientRect获取元素实际高度
        arr.push(item?.getBoundingClientRect()?.height ?? 0);
      }
    }
    // 更新高度列表
    setDomList(arr);
  }, [dataList]);

  return (
    <div>
      {/* 瀑布流容器，高度由计算得出 */}
      <div className={styles.container} style={{ height: `${maxHeight}rem` }}>
        {/* 渲染所有元素但设为不可见，用于获取元素高度 */}
        {dataList.map((item, index) => {
          // 如果已经获取了该元素的高度，则不需要再次渲染
          if (domList[index]) {
            return;
          }
          // 渲染原始卡片，用于获取高度
          return (
            <div
              className={styles.originCard}
              style={{ width: `calc(50% - ${pxtorem(margin.x / 2)})` }}
              key={index}
              data-index={index}>
              {childRender(item, index)}
            </div>
          );
        })}

        {/* 左列容器 */}
        <div
          className={styles.left}
          style={{ width: `calc(50% - ${pxtorem(margin.x / 2)})`, height: "100%" }}>
          {/* 使用虚拟列表渲染左列元素 */}
          {leftArr.length > 0 && (
            <VirtialList
              dataList={leftArr}
              itemHeight={index => {
                return leftHeightArr[index];
              }}
              childRender={childRender}
              marginY={margin?.y}
              shouldRenderChild={props.shouldRenderChild}
            />
          )}
        </div>
        
        {/* 右列容器 */}
        <div
          className={styles.right}
          style={{ width: `calc(50% - ${pxtorem(margin.x / 2)})`, height: "100%" }}>
          {/* 使用虚拟列表渲染右列元素 */}
          {rightArr.length > 0 && (
            <VirtialList
              dataList={rightArr}
              itemHeight={index => {
                return rightHeightArr[index];
              }}
              childRender={childRender}
              marginY={margin?.y}
              shouldRenderChild={props.shouldRenderChild}
            />
          )}
        </div>
      </div>
      {/* 底部渲染器（已注释掉） */}
      {/* {footerRender && maxHeight > 0 && footerRender()} */}
    </div>
  );
};

export default Waterfall;
